import { getRegionFromZipCode } from '@/data/regional-climate';

export type TopDressingMaterial = 'topsoil' | 'sand' | 'compost' | 'soil_mix' | 'peat';

export interface TopDressingInput {
  lawnSize_sqft: number;
  targetDepth_inches: number;
  material: TopDressingMaterial;
}

export interface TopDressingResult {
  volume_cubic_yards: number;
  volume_cubic_feet: number;
  weight_lbs_approx: number;
  bags_40lb: number;
  bags_1cf: number;
  coverage_note: string;
}

// Bulk density in lbs per cubic foot. Canonical source shared by the mobile
// screen and the /api/user/top-dressing save endpoint — do not diverge these
// without updating both surfaces. 70/30 sits between sand and compost; screened
// topsoil is a touch lighter than pure sand.
const DENSITY_LBS_PER_CF: Record<TopDressingMaterial, number> = {
  topsoil: 90,
  sand: 100,
  compost: 40,
  soil_mix: 75,
  peat: 25,
};

const MATERIAL_LABELS: Record<TopDressingMaterial, string> = {
  topsoil: 'Topsoil',
  sand: 'Sand',
  compost: 'Compost',
  soil_mix: 'Soil mix (70/30)',
  peat: 'Peat moss',
};

export function calculateTopDressing(input: TopDressingInput): TopDressingResult {
  const { lawnSize_sqft, targetDepth_inches, material } = input;
  const area = Number.isFinite(lawnSize_sqft) ? Math.max(0, lawnSize_sqft) : 0;
  const depth = Number.isFinite(targetDepth_inches) ? Math.max(0, targetDepth_inches) : 0;

  const depthFt = depth / 12;
  const volumeCf = area * depthFt;
  const volumeCy = volumeCf / 27;

  const density = DENSITY_LBS_PER_CF[material];
  const weightLbs = volumeCf * density;

  const bags40lb = Math.ceil(weightLbs / 40);
  const bags1cf = Math.ceil(volumeCf);

  const label = MATERIAL_LABELS[material];
  const coverage_note =
    `${round2(volumeCy)} cubic yards of ${label.toLowerCase()} ` +
    `covers ${area.toLocaleString()} sq ft at ${depth}" deep.`;

  return {
    volume_cubic_yards: round2(volumeCy),
    volume_cubic_feet: round2(volumeCf),
    weight_lbs_approx: Math.round(weightLbs),
    bags_40lb: bags40lb,
    bags_1cf: bags1cf,
    coverage_note,
  };
}

// ---------------------------------------------------------------------------
// Web-tool "order" model: the volume math above, plus per-material metadata,
// regional bulk-vs-bagged pricing, and the guidance notes the /tools page shows.
// Ported from the approved Claude Design spec ("Topsoil Calculator.dc.html",
// project 68b86b0d), upgraded to resolve the canonical named region from
// getRegionFromZipCode (like lib/sod/calculator.ts) instead of the prototype's
// first-ZIP-digit lookup, so region naming stays consistent site-wide.
// ---------------------------------------------------------------------------

export interface TopDressingMaterialMeta {
  id: TopDressingMaterial;
  /** Chip label, e.g. "Leveling sand". */
  label: string;
  /** One-line chip helper, e.g. "Smooth out bumps (Bermuda/Zoysia)". */
  sub: string;
  /** Display word used inside result copy, e.g. "leveling sand". */
  word: string;
  /** lbs per cubic foot — mirrors DENSITY_LBS_PER_CF. */
  density: number;
  /** $ per cubic yard delivered loose (national baseline; region multiplier scales it). */
  bulkPerYd: number;
  /** $ per 40 lb bag (national baseline). */
  bagPrice: number;
}

/** Single source of truth for the material chips + pricing inputs. */
export const TOP_DRESSING_MATERIALS: TopDressingMaterialMeta[] = [
  { id: 'topsoil', label: 'Topsoil', sub: 'Fill low areas, bed prep', word: 'screened topsoil', density: DENSITY_LBS_PER_CF.topsoil, bulkPerYd: 45, bagPrice: 4 },
  { id: 'sand', label: 'Leveling sand', sub: 'Smooth out bumps (Bermuda/Zoysia)', word: 'leveling sand', density: DENSITY_LBS_PER_CF.sand, bulkPerYd: 55, bagPrice: 6 },
  { id: 'compost', label: 'Compost', sub: 'Feed the soil, pre-seed boost', word: 'compost', density: DENSITY_LBS_PER_CF.compost, bulkPerYd: 50, bagPrice: 7 },
  { id: 'soil_mix', label: '70/30 mix', sub: 'Sand + compost, level and feed', word: '70/30 sand-compost mix', density: DENSITY_LBS_PER_CF.soil_mix, bulkPerYd: 52, bagPrice: 7 },
  { id: 'peat', label: 'Peat moss', sub: 'Seed cover, moisture holding', word: 'peat moss', density: DENSITY_LBS_PER_CF.peat, bulkPerYd: 90, bagPrice: 12 },
];

/**
 * Regional price multiplier keyed by the canonical region names returned by
 * getRegionFromZipCode. Unknown ZIPs fall back to 1.0 ("National average").
 * Soil/sand hauling tracks the same cost geography as sod, so the values mirror
 * lib/sod/calculator.ts SOD_REGION_MULT.
 */
const TOPSOIL_REGION_MULT: Record<string, number> = {
  Northeast: 1.18,
  'Mid-Atlantic': 1.05,
  Southeast: 0.9,
  'Great Lakes': 1.0,
  'Upper Midwest': 1.05,
  Central: 0.95,
  'South Central': 0.9,
  'Mountain West': 1.1,
  'West Coast': 1.25,
  'Pacific Northwest': 1.15,
  California: 1.25,
  'Desert Southwest': 1.1,
  Florida: 0.95,
  Hawaii: 1.35,
  Alaska: 1.35,
};

export interface TopDressingOrderInput {
  areaSqFt: number;
  depthInches: number;
  material: TopDressingMaterial;
  /** Optional: enables region-scaled pricing. */
  zipCode?: string;
}

export interface TopDressingCostRange {
  mid: number;
  low: number;
  high: number;
  /** "$120 – $180" */
  label: string;
}

export interface TopDressingOrder {
  material: TopDressingMaterial;
  materialLabel: string;
  materialWord: string;
  depthInches: number;
  areaSqFt: number;

  volumeCubicYards: number;
  volumeCubicFeet: number;
  weightLbs: number;
  /** "2.1 tons" or "1,850 lbs". */
  weightLabel: string;
  bags40lb: number;
  bags075cf: number;
  truckloads: number;

  region: string | null;
  regionKnown: boolean;
  /** "Southeast" or "National average". */
  regionLabel: string;
  mult: number;

  bulkCost: TopDressingCostRange;
  baggedCost: TopDressingCostRange;
  /** True when bulk delivery beats bagged at a meaningful volume (>= 1 cu yd). */
  bulkCheaper: boolean;
  savingsNote: string;
  bulkNote: string;
  baggedNote: string;

  /** True for sand / 70-30 mix — surfaces the Bermuda/Zoysia leveling callout. */
  showLeveling: boolean;
  depthNote: string;
  /** True at >= 1 in, where a living lawn gets buried (amber warning styling). */
  depthWarning: boolean;
}

function money(v: number): string {
  const rounded = v >= 10000 ? Math.round(v / 100) * 100 : Math.round(v / 10) * 10;
  return '$' + rounded.toLocaleString();
}

function costRange(mid: number): TopDressingCostRange {
  if (mid <= 0) return { mid: 0, low: 0, high: 0, label: '$0 – $0' };
  const low = Math.max(10, mid * 0.85);
  const high = Math.max(15, mid * 1.15);
  return { mid, low, high, label: `${money(low)} – ${money(high)}` };
}

function getMaterial(id: TopDressingMaterial): TopDressingMaterialMeta {
  return TOP_DRESSING_MATERIALS.find((m) => m.id === id) ?? TOP_DRESSING_MATERIALS[0];
}

export function calculateTopDressingOrder(input: TopDressingOrderInput): TopDressingOrder {
  const m = getMaterial(input.material);
  const area = Number.isFinite(input.areaSqFt) ? Math.max(0, input.areaSqFt) : 0;
  const depth = Number.isFinite(input.depthInches) ? Math.max(0, input.depthInches) : 0;

  const region = input.zipCode && /^\d{5}$/.test(input.zipCode) ? getRegionFromZipCode(input.zipCode) : 'Unknown';
  const regionKnown = region !== 'Unknown';
  const mult = regionKnown ? TOPSOIL_REGION_MULT[region] ?? 1.0 : 1.0;
  const regionLabel = regionKnown ? region : 'National average';

  const cuFt = (area * depth) / 12;
  const cuYd = cuFt / 27;
  const lbs = cuFt * m.density;
  const tons = lbs / 2000;
  const bags40lb = lbs > 0 ? Math.ceil(lbs / 40) : 0;
  const bags075cf = cuFt > 0 ? Math.ceil(cuFt / 0.75) : 0;
  const truckloads = cuYd > 0 ? Math.ceil(cuYd / 10) : 0;

  // Bulk = loose material by the yard + a flat delivery fee, both region-scaled.
  const bulkMid = cuYd > 0 ? Math.max(60, cuYd * m.bulkPerYd * mult) + 75 * mult : 0;
  const baggedMid = bags40lb * m.bagPrice * mult;
  const bulkCheaper = bulkMid < baggedMid && cuYd >= 1;

  const savingsNote =
    cuYd <= 0
      ? 'Enter an area greater than 0 square feet to calculate an order.'
      : cuYd < 1
      ? 'Under a cubic yard, bagged is usually the practical call: no delivery fee, no pile in the driveway, and you can haul it in a sedan.'
      : bulkMid < baggedMid
        ? `Bulk runs about ${money(baggedMid - bulkMid)} less than ${bags40lb.toLocaleString()} bags here, delivery included. The tradeoff: you need a driveway spot for the pile and a wheelbarrow day.`
        : 'At this size, bagged and bulk cost about the same. Bagged is easier to move and store; bulk saves the plastic and the trips.';

  const depthWarning = depth >= 1;
  const depthNote =
    depth >= 1
      ? `At ${fmtDepth(depth)}, existing grass will be buried and killed. That's fine for filling a bed or a hole, but for topdressing a living lawn stay at 1/2 inch or less per pass.`
      : depth >= 0.5
        ? '1/2 inch is the max a healthy lawn can grow through in one pass. If a spot needs more, do repeat passes 3 to 4 weeks apart.'
        : `At ${fmtDepth(depth)}, grass blades stay above the material and grow through in days. Safe for any healthy lawn.`;

  return {
    material: m.id,
    materialLabel: m.label,
    materialWord: m.word,
    depthInches: depth,
    areaSqFt: Math.round(area),

    volumeCubicYards: round2(cuYd),
    volumeCubicFeet: round2(cuFt),
    weightLbs: Math.round(lbs),
    weightLabel: tons >= 1 ? `${tons >= 10 ? Math.round(tons) : round1(tons)} tons` : `${Math.round(lbs).toLocaleString()} lbs`,
    bags40lb,
    bags075cf,
    truckloads,

    region: regionKnown ? region : null,
    regionKnown,
    regionLabel,
    mult,

    bulkCost: costRange(bulkMid),
    baggedCost: costRange(baggedMid),
    bulkCheaper,
    savingsNote,
    bulkNote: truckloads === 0 ? 'No delivery needed' : truckloads > 1 ? `${truckloads} truckloads, delivery included` : 'One delivery, dumped where you point',
    baggedNote: bags40lb === 0 ? 'No bags needed' : `${bags40lb.toLocaleString()} bags (40 lb) or ${bags075cf.toLocaleString()} bags (0.75 cu ft)`,

    showLeveling: m.id === 'sand' || m.id === 'soil_mix',
    depthNote,
    depthWarning,
  };
}

/** Format a decimal-inch depth as a friendly fraction where it maps cleanly. */
export function fmtDepth(inches: number): string {
  const FRACTIONS: Record<string, string> = {
    '0.125': '1/8 in',
    '0.25': '1/4 in',
    '0.375': '3/8 in',
    '0.5': '1/2 in',
    '1': '1 in',
    '2': '2 in',
  };
  return FRACTIONS[String(inches)] ?? `${round2(inches)} in`;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
